import { supabase } from "@/infrastructure/supabase/client";
import type { SprintRow } from "@/infrastructure/supabase/database.types";

/**
 * `created_by` is deliberately NOT picked from `SprintRow` (where it is
 * nullable): the insert policy requires it to equal auth.uid(), so a caller
 * without a session must fail to compile rather than fail at runtime.
 */
export type CreateSprintInput = Pick<SprintRow, "name" | "start_date" | "end_date"> & {
  created_by: string;
};

export const sprintsRepository = {
  /** Chronological — the page renders sprint sections in this order. */
  async list(): Promise<SprintRow[]> {
    const { data, error } = await supabase
      .from("sprints")
      .select("*")
      .order("start_date", { ascending: true });
    if (error) throw error;
    return data ?? [];
  },

  async create(input: CreateSprintInput): Promise<SprintRow> {
    // created_by must be the caller's auth.uid() or the insert policy rejects
    // the row (migration 0130).
    const { data, error } = await supabase.from("sprints").insert(input).select("*").single();
    if (error) throw error;
    return data;
  },

  async update(
    id: string,
    patch: Partial<Pick<SprintRow, "name" | "start_date" | "end_date">>
  ): Promise<SprintRow> {
    const { data, error } = await supabase
      .from("sprints")
      .update(patch)
      .eq("id", id)
      .select("*")
      .single();
    if (error) throw error;
    return data;
  },

  /** Tasks in the sprint fall back to the backlog (FK is on delete set null). */
  async remove(id: string): Promise<void> {
    const { error } = await supabase.from("sprints").delete().eq("id", id);
    if (error) throw error;
  },
};
